import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  yandexMapsLoadFailed,
  loadYandexMaps,
} from '@/lib/yandexMapsLoader'
import { YANDEX_MAPS_API_KEY } from '@/config'
import i18n from '@/utils/i18n'
import { useInspectorTrack, type TrackPoint } from '@/hooks/useInspectorTrack'

interface Props {
  orderId: string
  inspectorId: string
  orderCreatedAt: string
  /** Если заказ ещё активен — рисуем live-обновление; если завершён —
   *  загружаем один раз. */
  isOrderActive: boolean
  /** Координаты адреса заказа — рисуем маркером если есть. */
  orderLat?: number | null
  orderLon?: number | null
}

/**
 * Секция «Трек инспектора» на странице заказа в админке.
 *
 * UX: по умолчанию collapsed — клик «Показать» монтирует Yandex Map и
 * подгружает точки. Это экономит ресурс — большинство менеджеров на
 * страницу заказа заходят без интереса к треку.
 *
 * Период автоматический: с `order.created_at` до `now()`, фильтр по
 * `inspector_position_history.order_id = orderId`. Сам хук берёт точки
 * через RLS — admin/dispatcher напрямую, manager-создатель через
 * VIEW `inspector_track_for_dealer`.
 */
export function OrderInspectorTrackBlock({
  orderId,
  inspectorId,
  orderCreatedAt,
  isOrderActive,
  orderLat,
  orderLon,
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const range = useMemo(
    () => ({ from: orderCreatedAt, to: new Date().toISOString() }),
    [orderCreatedAt],
  )

  const trackQ = useInspectorTrack({
    inspectorId: open ? inspectorId : undefined,
    from: range.from,
    to: range.to,
    orderId,
    liveUpdate: open && isOrderActive,
  })

  const points: TrackPoint[] = trackQ.data ?? []

  if (!open) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('orderBlocks.track.intro')}
          {isOrderActive ? ' ' + t('orderBlocks.track.liveHint') : ''}
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={!YANDEX_MAPS_API_KEY}
          className="rounded border border-[color:var(--stryzh-color-orange)]/50 px-3 py-1.5 text-sm text-[color:var(--stryzh-color-orange)] disabled:opacity-50"
        >
          {t('orderBlocks.track.show')}
        </button>
      </div>
    )
  }

  return (
    <TrackMap
      points={points}
      isLoading={trackQ.isLoading}
      error={trackQ.error as Error | null}
      orderLat={orderLat ?? null}
      orderLon={orderLon ?? null}
      onClose={() => setOpen(false)}
      onRefresh={() => trackQ.refetch()}
      isFetching={trackQ.isFetching}
    />
  )
}

interface TrackMapProps {
  points: TrackPoint[]
  isLoading: boolean
  error: Error | null
  orderLat: number | null
  orderLon: number | null
  onClose: () => void
  onRefresh: () => void
  isFetching: boolean
}

function TrackMap({
  points,
  isLoading,
  error,
  orderLat,
  orderLon,
  onClose,
  onRefresh,
  isFetching,
}: TrackMapProps) {
  const { t } = useTranslation()
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layerRef = useRef<any[]>([])
  const [mapReady, setMapReady] = useState(false)
  const [mapLoadError, setMapLoadError] = useState<string | null>(null)

  useLayoutEffect(() => {
    if (!YANDEX_MAPS_API_KEY) return
    const el = mapContainerRef.current
    if (!el) return
    let cancelled = false
    void (async () => {
      try {
        await loadYandexMaps()
      } catch (e) {
        if (!cancelled)
          setMapLoadError(
            e instanceof Error ? e.message : yandexMapsLoadFailed(),
          )
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ymaps = (window as unknown as any).ymaps
      if (cancelled || !ymaps || mapRef.current) return
      const center: [number, number] =
        orderLat != null && orderLon != null
          ? [orderLat, orderLon]
          : [55.751244, 37.618423]
      const map = new ymaps.Map(
        el,
        { center, zoom: 11, controls: ['zoomControl', 'fullscreenControl'] },
        { suppressMapOpenBlock: true },
      )
      mapRef.current = map
      setMapReady(true)
    })()

    return () => {
      cancelled = true
      try {
        mapRef.current?.destroy?.()
      } catch {
        /* empty */
      }
      mapRef.current = null
      layerRef.current = []
    }
  }, [orderLat, orderLon])

  useEffect(() => {
    if (!mapReady) return
    const map = mapRef.current
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ymaps = (window as unknown as any).ymaps
    if (!map || !ymaps) return

    for (const obj of layerRef.current) {
      try {
        map.geoObjects.remove(obj)
      } catch {
        /* empty */
      }
    }
    layerRef.current = []

    // Маркер адреса заказа.
    if (orderLat != null && orderLon != null) {
      const addrPm = new ymaps.Placemark(
        [orderLat, orderLon],
        { balloonContent: t('orderBlocks.track.orderAddress') },
        { preset: 'islands#blueDotIcon' },
      )
      map.geoObjects.add(addrPm)
      layerRef.current.push(addrPm)
    }

    if (points.length === 0) return

    // Все точки одним сегментом — заказы в active_order по определению,
    // их order_id == orderId. Цвет — оранжевый.
    const coords = points.map((p) => [p.lat, p.lon] as [number, number])
    if (coords.length >= 2) {
      const line = new ymaps.Polyline(
        coords,
        {},
        {
          strokeColor: '#F7931A',
          strokeWidth: 4,
          strokeStyle: 'solid',
          interactivityModel: 'default#transparent',
        },
      )
      map.geoObjects.add(line)
      layerRef.current.push(line)
    }

    const first = points[0]
    const last = points[points.length - 1]
    const locale = i18n.language === 'en' ? 'en-US' : 'ru-RU'
    const startPm = new ymaps.Placemark(
      [first.lat, first.lon],
      {
        balloonContent: t('orderBlocks.track.start', {
          time: new Date(first.recorded_at).toLocaleString(locale),
        }),
      },
      { preset: 'islands#greenCircleDotIcon' },
    )
    const endPm = new ymaps.Placemark(
      [last.lat, last.lon],
      {
        balloonContent: t('orderBlocks.track.finish', {
          time: new Date(last.recorded_at).toLocaleString(locale),
        }),
      },
      { preset: 'islands#redCircleDotIcon' },
    )
    map.geoObjects.add(startPm)
    map.geoObjects.add(endPm)
    layerRef.current.push(startPm, endPm)

    // Авто-zoom: bounds по точкам + адресу заказа.
    const lats = [...points.map((p) => p.lat)]
    const lons = [...points.map((p) => p.lon)]
    if (orderLat != null && orderLon != null) {
      lats.push(orderLat)
      lons.push(orderLon)
    }
    const bounds: [[number, number], [number, number]] = [
      [Math.min(...lats), Math.min(...lons)],
      [Math.max(...lats), Math.max(...lons)],
    ]
    try {
      map.setBounds(bounds, { checkZoomRange: true, zoomMargin: 40 })
    } catch {
      /* empty */
    }
  }, [points, mapReady, orderLat, orderLon, t])

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {isLoading
            ? t('orderBlocks.track.loading')
            : t('orderBlocks.track.pointsCount', { count: points.length })}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={isFetching}
            className="rounded border border-white/15 px-3 py-1.5 text-sm text-[color:var(--stryzh-color-text-primary)] disabled:opacity-50"
          >
            {isFetching ? t('orderBlocks.track.refreshing') : t('orderBlocks.track.refresh')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-white/15 px-3 py-1.5 text-sm text-[color:var(--stryzh-color-text-secondary)]"
          >
            {t('orderBlocks.track.hide')}
          </button>
        </div>
      </div>
      {error ? (
        <div className="mb-2 rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          {error.message}
        </div>
      ) : null}
      {mapLoadError ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          {mapLoadError}
        </div>
      ) : (
        <div
          ref={mapContainerRef}
          style={{ width: '100%', height: 360, borderRadius: 6 }}
        />
      )}
    </div>
  )
}
